import Vue from 'vue'
import _ from 'underscore'
import Chapter from '../../resources/chapter'
import MicroNote from '../../resources/microNote'
import moment from 'moment'

// https://vuex.vuejs.org/en/modules.html


const utils = {
  resetState(state) {
    state.chapterId = null; // current chapter we're studying
    state.currentMicroNoteIndex = 0; // current micro note we're studying
    state.currentKeywordUID = null; // which keyword we're on
    state.keywords = {}; // map of keywords. key is the _uid for the component
  },

  unansweredKeywords(state) {
    const keywords = _.values(state.keywords);
    const unanswered = _.filter(keywords, (keyword) => {
      return !keyword.answered;
    })
    return unanswered;
  },

  // Returns the next unanswered keyword or null if all are answered
  nextKeyword: (state) => {
    const unanswered = utils.unansweredKeywords(state);
    if( !unanswered || !unanswered.length) return null;
    if(unanswered.length === 1) return unanswered[0];

    let nextKeyword;
    const nextKeywordIndex = _.findIndex(unanswered, {_uid: state.currentKeywordUID + 1}); // hacks!
    nextKeyword = unanswered[nextKeywordIndex] || unanswered[0];
    return nextKeyword;
  },

  /**
   * Determine if answer is correct by normalizing input
   */

  answersMatch(answer1, answer2) {
    if( !_.isString(answer1) || !_.isString(answer2)) return false;
    return answer1.toLowerCase().trim() === answer2.toLowerCase().trim();
  },
}


const state = {};
utils.resetState(state);



const getters = {
  chapter(state, getters, rootState, rootGetters) {
    if( !state.chapterId || !rootState.chapters) return null;
    return rootState.chapters[state.chapterId];
  },

  // The microNotes *left* to be studied
  microNotes(state, getters, rootState, rootGetters) {
    // possible #todo - is use rootGetters.unstudiedMicroNotes... depends if we want
    // the option to re-study unstudied

    if( !state.chapterId || !_.isArray(rootGetters.allMicroNotes)) return [];
    let microNotes = _.where(rootGetters.allMicroNotes, {chapter_id: parseInt(state.chapterId)});

    // Limit microNotes to only ones that need to be studied
    const now = moment();
    microNotes = _.filter(microNotes, (microNote) => {
      if (microNote.active === false) return false;
      if( !microNote.next_study_at) return true;

      const inThePast = now.diff(microNote.next_study_at) >= 0;
      if(inThePast) return true;
    })


    // TODO: For some reason, micronotes are being re-sorted when they come back out of the state. When going in, they are the correct order
    // as received from the API. The following code does client side ordering based on the user setting
    // the API is already handling this but the State getters override it!
    let sortOrder = 'ASC';
    let studySort = 'created_at';

    if (rootState.currentUser) {
      sortOrder = rootState.currentUser.sortOrder;
      studySort = rootState.currentUser.studySort;
    }

    let sortInt = sortOrder === 'ASC' ? -1 : 1;
    
    if (studySort === 'created_at') {
      // sort by created date
      microNotes.sort((a, b) => {
        if (!a.created_at) return sortInt;
        if (!b.created_at) return sortInt*-1;

        const diff = moment(a.created_at).diff(b.created_at);
        if (diff < 0) return sortInt;
        if (diff > 0) return sortInt*-1;

        // if we got here, the date is the same so order by ID
        if (a.id < b.id) return sortInt;
        else return sortInt * -1;
      });
    } else if (studySort === 'next_study_at') {
      // sort by next study date
      microNotes.sort((a, b) => {
        if (!a.next_study_at) return sortInt;
        if (!b.next_study_at) return sortInt*-1;

        const diff = moment(a.next_study_at).diff(b.next_study_at);
        if (diff < 0) return sortInt;
        if (diff > 0) return sortInt*-1;

        // if we got here then the next study at was the same
        // so order by ID
        if (a.id < b.id) return sortInt;
        else return sortInt * -1;
      });
    }

    return microNotes;
  },

  currentMicroNote: (state, getters) => {
    if (!getters.microNotes) return null;
    return getters.microNotes[state.currentMicroNoteIndex];
  },

  wordbankWords(state, getters, rootState, rootGetters) {
    // Load wordbank (not sure if it's kosher to use getter here)
    const microNote = getters.currentMicroNote;
    if( !microNote) return [];
    let words = [];


    if(microNote.micro_note_type === 'html') {
      $(microNote.html).find('keyword').each((i, el) => {
        // console.log(`'${$(el).html()}'`)
        words.push($(el).html());
      });
    } else {
      words = _.map(microNote.keywords, (keyword) => { return keyword.value; })
    }

    // #todo - eventually mix this with random words
    return _.shuffle(words);
  },

  currentKeyword: (state) => {
    return state.keywords[state.currentKeywordUID];
  },

  microNoteIsCompleted: (state) => {
    const remaining = utils.unansweredKeywords(state).length;
    if( !remaining) return true;
  },
}

const actions = {


  nextMicroNote({ commit, state, getters }, payload) {
    let nextIndex = state.currentMicroNoteIndex + 1;
    if (!getters.microNotes[nextIndex]) nextIndex = 0;
    commit('SET_ACTIVE_MICRO_NOTE', { microNoteIndex: nextIndex });
  },

  previousMicroNote({ commit, state, getters }, payload) {
    let nextIndex = state.currentMicroNoteIndex - 1;
    if( !getters.microNotes[nextIndex]) nextIndex = getters.microNotes.length - 1;
    commit('SET_ACTIVE_MICRO_NOTE', { microNoteIndex: nextIndex });
  },

  nextKeyword({ commit, state, getters }) {
    const currentKeyword = getters.currentKeyword;

    // When we advance, make sure the previous keyword is marked as answered if it has content
    if(currentKeyword && currentKeyword.answer && !currentKeyword.answered) {
      commit('UPDATE_KEYWORD', {
        _uid: currentKeyword._uid,
        changes: {
          answered: true,
          correct: utils.answersMatch(currentKeyword.answer, currentKeyword.realAnswer),
        },
      });
    }

    const nextKeyword = utils.nextKeyword(state);
    commit('SET_ACTIVE_KEYWORD', {keyword: nextKeyword})
  },

  setActiveKeyword({ commit, state, getters }, payload) {
    const currentKeyword = getters.currentKeyword;
    // When we change keyword, make sure the previous keyword is marked
    // as answered if it has content
    if(currentKeyword && currentKeyword.answer && !currentKeyword.answered) {
      commit('UPDATE_KEYWORD', {
        _uid: currentKeyword._uid,
        changes: {
          answered: true,
          correct: utils.answersMatch(currentKeyword.answer, currentKeyword.realAnswer),
        },
      });
    }

    // console.log('studier/setActiveKeyword: ', payload.keyword);
    commit('SET_ACTIVE_KEYWORD', payload)
  },

  // Answers the keyword fully and moves to the next one
  // payload.answer
  answerKeyword({ commit, state, dispatch, getters }, payload) {
    let currentKeyword = getters.currentKeyword;

    // not sure if we should do this - auto fill first keyword
    // if none is selected?
    //if( !currentKeyword) return;
     if( !currentKeyword) {
       dispatch('nextKeyword')
       currentKeyword = getters.currentKeyword;
     }

    const keywordUID = currentKeyword._uid;
    // console.log('studier/answerKeyword: ', payload.answer);
    commit('UPDATE_KEYWORD', {
      _uid: keywordUID,
      changes: {
        answer: payload.answer,
        answered: payload.answer ? true : false,
        correct: utils.answersMatch(payload.answer, currentKeyword.realAnswer),
      },
    });
    dispatch('nextKeyword')
  },


}

const mutations = {
  // Note that this is namespaced, so it must be triggered by `studier/LOGOUT_SUCCESS`
  LOGOUT_SUCCESS() {
    utils.resetState(state);
  },

  RESET_KEYWORDS(state, payload) {
    state.keywords = {};
    state.currentKeywordUID = null;
  },

  // RESET(state, payload) {
  //   console.log('``````````````RESET')
  //   utils.resetState(state);
  // },

  // Loads a chapter into the studier. Run on pageload
  LOAD_CHAPTER(state, payload) {
    state.chapterId = payload.chapterId;
  },




  REGISTER_KEYWORD(state, payload) {
    const keyword = payload.keyword;
    Vue.set(state.keywords, keyword._uid, keyword);

    // console.log('REGISTER_KEYWORD')
    // console.log(payload.keyword)

    // If this is the first and we have no active keyword we'll activate it
    if( !state.currentKeywordUID) {
      state.currentKeywordUID = keyword._uid;
    }
  },

  SET_ACTIVE_MICRO_NOTE(state, payload) {
    const microNoteIndex = payload.microNoteIndex;

    if (microNoteIndex !== state.currentMicroNoteIndex) {
      Vue.set(state, 'keywords', {});
      Vue.set(state, 'currentMicroNoteIndex', microNoteIndex);
      Vue.set(state, 'currentKeywordUID', null);
    } else {
      Vue.set(state, 'currentKeywordUID', null);

      const keywords = _.values(state.keywords);
      for (let i = 0; i < keywords.length; i++) {
        const key = keywords[i];

        const newKeyword = Object.assign({}, key, {
          answer: null,
          answered: false,
          correct: null,
        });
        Vue.set(state.keywords, key._uid, newKeyword);
      }

    }
  },

  // payload.changes
  UPDATE_KEYWORD(state, payload) {
    // console.log('UPDATE_KEYWORD')
    // console.log(payload)

    const uid = payload._uid;
    const changes = payload.changes;
    // console.log(`answer keyword ${state.currentKeywordUID} with ${answer}`)
    const newKeyword = Object.assign({}, state.keywords[uid], changes)
    Vue.set(state.keywords, state.currentKeywordUID, newKeyword);
  },

  SET_ACTIVE_KEYWORD(state, payload) {
    if( !payload.keyword) {
      state.currentKeywordUID = null;
      return;
    }
    const keywordUID = payload.keyword._uid;
    state.currentKeywordUID = keywordUID;
  },
}


export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/studier.js